import React from 'react';

import type { IntlShape } from 'react-intl';
// oxlint-disable-next-line @atlassian/no-restricted-imports
import { lazyForPaint, LazySuspense } from 'react-loosely-lazy';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import type {
	ExtensionManifest,
	ExtensionParams,
	ReferenceEntity,
	ToolbarItem,
} from '@atlaskit/editor-common/extensions';
import { type EditorActions } from '@atlaskit/editor-core';
import {
	type ADFReference,
	ADFReferenceTypes,
	type ExtensionHandlerWithReferenceFn,
} from '@atlassian/editor-referentiality';

import { i18n } from '../messages';
import type { ParseNumber } from '../types';
import type { ChartParameters } from '../ui/charts/types';
import { defaultParseNumber } from '../utils';

import { chartToolbar } from './chartToolbar';
import { uiFields } from './configPanel';
import { tableToolbar } from './tableToolbar';

const LazyChart = lazyForPaint(() =>
	import(/* webpackChunkName: "@atlaskit-internal_charts_ui" */ '../ui/charts/index').then(
		(m) => m.Chart,
	),
);

const Skeleton: React.FC<{ height: string | number | undefined }> = ({ height }) => (
	<div style={{ height }}></div>
);

type ChartManifest = {
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
	editorActions?: EditorActions;
	extensionHandlerWithReference?: ExtensionHandlerWithReferenceFn<ChartParameters>;
	externalToolbarItems?: ToolbarItem[];
	featureFlags?: { [flag: string]: any };
	intl: IntlShape;
	onInsertButton?: (sourceNode: any) => void;
	parseNumber?: ParseNumber;
};

export const buildManifest = ({
	intl,
	editorActions,
	parseNumber,
	extensionHandlerWithReference,
	createAnalyticsEvent,
	onInsertButton,
	featureFlags,
	externalToolbarItems,
}: ChartManifest): ExtensionManifest => ({
	title: intl ? intl.formatMessage(i18n.extensionTitle) : i18n.extensionTitle.defaultMessage,
	type: 'com.atlassian.chart',
	key: 'chart',
	icons: {
		'48': () =>
			import(
				/* webpackChunkName: "@atlaskit-internal_charts_ChartIcon" */ '@atlaskit/icon/core/chart-bar'
			),
	},
	modules: {
		contextualToolbars: [
			tableToolbar({
				intl,
				parseNumber: parseNumber || defaultParseNumber,
				createAnalyticsEvent,
				onInsertButton,
				featureFlags,
			}),
			chartToolbar({ intl, editorActions, externalToolbarItems }),
		],

		nodes: {
			default: {
				type: 'extension',
				render: () => {
					const chartComponent = (
						node: ExtensionParams<ChartParameters>,
						references: ReferenceEntity[],
					) => (
						<LazySuspense
							fallback={
								<Skeleton
									height={node.parameters?.chartGroup?.customizeTab?.styleField?.height || 350}
								/>
							}
						>
							<LazyChart
								data={(references[0] as ADFReference)?.[ADFReferenceTypes.TABLE]}
								parseNumber={parseNumber}
								intl={intl}
								localId={node.localId}
								{...node.parameters}
							/>
						</LazySuspense>
					);

					return Promise.resolve(({ node, references, refNode }) => {
						if (refNode) {
							references = [refNode];
						}

						if (extensionHandlerWithReference) {
							return extensionHandlerWithReference(node, ({ references: referencesFromHandler }) =>
								chartComponent(node, referencesFromHandler),
							);
						}

						return chartComponent(node, references);
					});
				},
				getFieldsDefinition: async () => {
					const fields = uiFields(intl, editorActions);
					return (parameters) => fields(parameters);
				},
			},
		},
	},
});
